// base
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';

interface SignaturePadProps extends React.CanvasHTMLAttributes<HTMLCanvasElement> {
  imgType?: 'png' | 'jpg';
  dotSize?: number;
  handleSignatureEnd: (image: string, canvas: HTMLCanvasElement) => void;
}

export const SignaturePad = forwardRef((props: SignaturePadProps, ref) => {
  const { imgType = 'png', dotSize = 4, handleSignatureEnd, ...rest } = props;

  const signatureRef = useRef<HTMLCanvasElement>(null);

  const [isPainting, setIsPainting] = useState(false);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | undefined>(
    undefined
  );

  useImperativeHandle(ref, () => ({
    clearSignature
  }));

  const clearSignature = useCallback(() => {
    const canvas = signatureRef.current;
    const context = canvas?.getContext('2d');

    if (context) {
      context.clearRect(0, 0, canvas?.width as number, canvas?.height as number);
    }
  }, []);

  const getEventPosition = (event: MouseEvent | TouchEvent) => {
    const canvas = signatureRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;

    if (event instanceof TouchEvent) {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else {
      clientX = event.clientX;
      clientY = event.clientY;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const drawLine = useCallback(
    (
      originalMousePosition: { x: number; y: number },
      newMousePosition: { x: number; y: number }
    ) => {
      const canvas = signatureRef.current;
      if (!canvas) return;

      const context = canvas.getContext('2d');
      if (!context) return;

      context.beginPath();
      context.moveTo(originalMousePosition.x, originalMousePosition.y);
      context.lineTo(newMousePosition.x, newMousePosition.y);
      context.strokeStyle = '#000000';
      context.lineWidth = dotSize;
      context.stroke();
      context.closePath();
    },
    [dotSize]
  );

  useEffect(() => {
    const canvas = signatureRef.current;
    if (!canvas) return;

    const handleStart = (event: MouseEvent | TouchEvent) => {
      event.preventDefault();
      setIsPainting(true);
      const position = getEventPosition(event);
      if (position) setMousePosition(position);
    };

    const handleMove = (event: MouseEvent | TouchEvent) => {
      event.preventDefault();
      if (!isPainting) return;
      const newPosition = getEventPosition(event);
      if (mousePosition && newPosition) {
        drawLine(mousePosition, newPosition);
        setMousePosition(newPosition);
      }
    };

    const handleEnd = (event: MouseEvent | TouchEvent) => {
      event.preventDefault();
      setIsPainting(false);
      setMousePosition(undefined);
      const signatureImage = canvas.toDataURL(`image/${imgType}`, 1.0);
      handleSignatureEnd(signatureImage, canvas);
    };

    canvas.addEventListener('mousedown', handleStart);
    canvas.addEventListener('mousemove', handleMove);
    canvas.addEventListener('mouseup', handleEnd);
    canvas.addEventListener('mouseleave', handleEnd);

    canvas.addEventListener('touchstart', handleStart, { passive: false });
    canvas.addEventListener('touchmove', handleMove, { passive: false });
    canvas.addEventListener('touchend', handleEnd, { passive: false });

    return () => {
      canvas.removeEventListener('mousedown', handleStart);
      canvas.removeEventListener('mousemove', handleMove);
      canvas.removeEventListener('mouseup', handleEnd);
      canvas.removeEventListener('mouseleave', handleEnd);

      canvas.removeEventListener('touchstart', handleStart);
      canvas.removeEventListener('touchmove', handleMove);
      canvas.removeEventListener('touchend', handleEnd);
    };
  }, [isPainting, mousePosition, dotSize, imgType, handleSignatureEnd, drawLine]);

  useEffect(() => {
    const canvas = signatureRef.current;
    const context = canvas?.getContext('2d');

    if (context) {
      context.strokeStyle = '#000000';
      context.lineJoin = 'round';
      context.lineWidth = dotSize;
    }
  }, [dotSize]);

  return (
    <canvas ref={signatureRef} width={props.width || 300} height={props.height || 150} {...rest} />
  );
});

SignaturePad.displayName = 'SignaturePad';
